import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLobbySocket } from '../contexts/LobbySocketContext';
import { useTranslation } from '../contexts/LanguageContext';
import { useTrial } from '../contexts/TrialContext';
import { TitleBadgeInline } from './KingdomTitle';
import SpectatorRequestPending from './SpectatorRequestPending';
import '../styles/MultiplayerLobby.css';
import '../styles/SpectatorRequest.css';
import logger from '../utils/logger';

const FREE_VARIANTS = ['standard', 'fight_to_be_king'];

const MultiplayerLobby = ({ 
  onClose, 
  onGameStart,
  globalNotifications = [],
  addGlobalNotification,
  clearGlobalNotification,
  clearAllGlobalNotifications
}) => {
  // Use translations
  const { t } = useTranslation();
  
  // Use centralized lobby WebSocket
  const { connected: lobbyConnected, addMessageHandler } = useLobbySocket();
  
  // Get user from auth context
  const { user, token } = useAuth();
  const { hasActiveAccess } = useTrial();
  const hasPremium = hasActiveAccess();
  
  // Spectator request state
  const [pendingSpectatorRequest, setPendingSpectatorRequest] = useState(null);
  
  // Debug: Check if onGameStart is a function
  React.useEffect(() => {
  }, [onGameStart, lobbyConnected]);

  // Wrapper for onGameStart with logging
  const handleGameRedirect = useCallback((gameId, mode, roomName = null) => {
    if (typeof onGameStart === 'function') {
      onGameStart(gameId, mode, roomName);
    } else {
      logger.error('❌ onGameStart is not a function!');
    }
  }, [onGameStart]);

  const [activeTab, setActiveTab] = useState('quick'); // 'quick', 'private', 'join', 'spectate'
  const [roomCode, setRoomCode] = useState('');
  const [variant, setVariant] = useState('fight_to_be_king');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdRoomCode, setCreatedRoomCode] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [spectateGames, setSpectateGames] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [joinedRooms, setJoinedRooms] = useState([]);
  const [roomName, setRoomName] = useState('');
  const [spectatorMode, setSpectatorMode] = useState('private');
  const [preferredColor, setPreferredColor] = useState('white'); // white, black, random
  const [variantFilter, setVariantFilter] = useState('');
  const [spectatorRequests, setSpectatorRequests] = useState({}); // gameId -> [{user_id, name, avatar}]
  const [moveTimer, setMoveTimer] = useState(null); // null = no limit, or seconds (15, 30, 60, 120, 300)
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [challengeRoomId, setChallengeRoomId] = useState(null);
  const [friendsList, setFriendsList] = useState([]);
  const [directChallengeMode, setDirectChallengeMode] = useState(false);
  const [challengeVariant, setChallengeVariant] = useState('instant_king');
  const [challengeTimer, setChallengeTimer] = useState(null);
  const [pendingChallenges, setPendingChallenges] = useState({ incoming: [], outgoing: [] });
  const [challengeProcessing, setChallengeProcessing] = useState(null);
  const roomCodeInputRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  // Local notification state (for backward compatibility)
  const [localNotifications, setLocalNotifications] = useState([]);
  const notificationIdCounter = useRef(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Merge global and local notifications
  const notifications = [...(globalNotifications || []), ...localNotifications];

  // Auto-focus room code input when join tab is active
  useEffect(() => {
    if (activeTab === 'join' && roomCodeInputRef.current) {
      roomCodeInputRef.current.focus();
    }
  }, [activeTab]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Notification management functions - prefer global if available
  const addNotification = useCallback((type, gameId, roomName, message) => {
    if (addGlobalNotification) {
      // Use global notifications (persists across views)
      addGlobalNotification(type, gameId, roomName, message);
    } else {
      // Fallback to local notifications
      const newNotification = {
        id: `notif-${Date.now()}-${notificationIdCounter.current++}`,
        type,
        gameId,
        roomName,
        message,
        timestamp: new Date().toISOString(),
        read: false
      };

      setLocalNotifications(prev => {
        const updated = [newNotification, ...prev];
        if (updated.length > 30) {
          return updated.slice(0, 30);
        }
        return updated;
      });
    }
  }, [addGlobalNotification]);

  const dismissNotification = useCallback((notificationId) => {
    if (clearGlobalNotification) {
      clearGlobalNotification(notificationId);
    }
    setLocalNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, [clearGlobalNotification]);

  const clearAllNotifications = useCallback(() => {
    if (clearAllGlobalNotifications) {
      clearAllGlobalNotifications();
    }
    setLocalNotifications([]);
  }, [clearAllGlobalNotifications]);

  const handleEnterRoomFromNotification = useCallback((gameId, roomName) => {
    // Navigate to room
    handleGameRedirect(gameId, 'multiplayer', roomName);
  }, [handleGameRedirect]);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Helper function to get current visual settings from localStorage
  const getCurrentVisualSettings = () => {
    try {
      const saved = localStorage.getItem('chessKingdomSettings');
      if (saved) {
        const settings = JSON.parse(saved);
        return {
          piece_skin_white: settings.pieceSkinWhite || null,
          piece_skin_black: settings.pieceSkinBlack || null,
          duke_skin_white: settings.dukeSkinWhite || 'default',
          duke_skin_black: settings.dukeSkinBlack || 'default',
          board_theme: settings.boardTheme || 'default',
          // Include piece colors for sync
          piece_theme: settings.pieceTheme || 'classic',
          piece_color_white: settings.customPieceWhite || null,
          piece_color_black: settings.customPieceBlack || null,
          use_custom_pieces: settings.useCustomPieces || false,
          // Include coordinate color
          coordinate_color: settings.coordinateColor || '#d4af37',
          // Include grid settings for sync (creator's settings apply to all)
          grid_settings: settings.gridSettings || null
        };
      }
    } catch (e) {
      logger.warn('Failed to load visual settings:', e);
    }
    return {
      piece_skin_white: null,
      piece_skin_black: null,
      duke_skin_white: 'default',
      duke_skin_black: 'default',
      board_theme: 'default',
      piece_theme: 'classic',
      piece_color_white: null,
      piece_color_black: null,
      use_custom_pieces: false,
      coordinate_color: '#d4af37',
      grid_settings: null
    };
  };

  // Quick Match
  const handleQuickMatch = async () => {
    clearMessages();
    setLoading(true);
    setIsSearching(true);

    // Get user's current visual settings to pass for Quick Match
    const visualSettings = getCurrentVisualSettings();
    const queryParams = new URLSearchParams({
      variant: variant,
      piece_skin_white: visualSettings.piece_skin_white || '',
      piece_skin_black: visualSettings.piece_skin_black || '',
      duke_skin_white: visualSettings.duke_skin_white,
      duke_skin_black: visualSettings.duke_skin_black,
      board_theme: visualSettings.board_theme
    });

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/matchmaking/queue?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        if (data.matched) {
          // Instant match!
          setIsSearching(false);
          setLoading(false);
          const variantName = variant.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
          handleGameRedirect(data.game_id, 'multiplayer', `${variantName} Match`);
        } else {
          // Poll for match
          startPolling();
        }
      } else {
        setError(data.detail || t('errors.failedToJoinMatchmaking'));
        setIsSearching(false);
      }
    } catch (error) {
      setError(t('errors.connectionErrorInternet'));
      setIsSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    // Set a 60-second timeout for matchmaking
    searchTimeoutRef.current = setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      setIsSearching(false);
      setError(t('errors.noOpponentFound'));
      cancelSearch();
    }, 60000);

    pollIntervalRef.current = setInterval(async () => {
      try {
        // Pass the variant to the status check to only match games with the same variant
        const response = await fetch(`${API_URL}/api/multiplayer/matchmaking/status?variant=${variant}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.matched) {
          clearInterval(pollIntervalRef.current);
          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
          }
          setIsSearching(false);
          setLoading(false);
          const variantName = variant.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
          handleGameRedirect(data.game_id, 'multiplayer', `${variantName} Match`);
        }
      } catch (error) {
        logger.error('Polling error:', error);
      }
    }, 2000);
  };

  const cancelSearch = async () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    try {
      await fetch(`${API_URL}/api/multiplayer/matchmaking/queue`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      logger.error('Cancel error:', error);
    }

    setIsSearching(false);
  };

  // Create Private Room
  const handleCreatePrivateRoom = async () => {
    if (!roomName.trim()) {
      setError(t('errors.pleaseEnterRoomName'));
      return;
    }

    clearMessages();
    setLoading(true);

    // Get user's current visual settings to pass as host settings
    const visualSettings = getCurrentVisualSettings();

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          room_name: roomName.trim(),
          is_private: true,
          variant: variant,
          white_embodiment: 'instant_king',
          black_embodiment: 'instant_king',
          spectator_mode: spectatorMode,
          preferred_color: preferredColor,
          move_timer: moveTimer, // Timer setting for moves
          // Host visual settings for multiplayer sync
          piece_skin_white: visualSettings.piece_skin_white,
          piece_skin_black: visualSettings.piece_skin_black,
          duke_skin_white: visualSettings.duke_skin_white,
          duke_skin_black: visualSettings.duke_skin_black,
          board_theme: visualSettings.board_theme,
          // Piece colors for sync
          piece_theme: visualSettings.piece_theme,
          piece_color_white: visualSettings.piece_color_white,
          piece_color_black: visualSettings.piece_color_black,
          use_custom_pieces: visualSettings.use_custom_pieces,
          // Coordinate color for sync
          coordinate_color: visualSettings.coordinate_color,
          // Grid settings for sync (creator's settings apply to all)
          grid_settings: visualSettings.grid_settings
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCreatedRoomCode(data.room_code);
        const timerText = moveTimer ? ` (${moveTimer}s per move)` : '';
        setSuccess(`Room "${data.room_name}" created!${timerText} Share code: ${data.room_code}`);
        loadMyRooms(); // Refresh my rooms list
        
        // Auto-enter the room after creation
        setTimeout(() => {
          handleGameRedirect(data.game_id, 'multiplayer', data.room_name);
        }, 1500);
      } else {
        setError(data.detail || t('errors.failedToCreateRoom'));
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Join Private Room
  const handleJoinRoom = async () => {
    if (!roomCode.trim()) {
      setError(t('errors.pleaseEnterRoomCode'));
      return;
    }

    if (roomCode.length !== 6) {
      setError(t('errors.roomCodeMustBe6Chars'));
      return;
    }

    clearMessages();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ room_code: roomCode.toUpperCase() })
      });

      const data = await response.json();

      if (response.ok) {
        // Check if this is a pending approval response
        if (data.pending_approval) {
          setSuccess(data.message || t('success.joinRequestSent', 'Your join request has been sent to the room host. Please wait for approval.'));
          setRoomCode(''); // Clear code input
          // Don't redirect - user should wait for approval notification
          return;
        }
        // Apply host's visual settings
        if (data.host_settings) {
          const hs = data.host_settings;
          // Store in localStorage so App.js can pick them up
          try {
            const currentSettings = JSON.parse(localStorage.getItem('chessKingdomSettings') || '{}');
            currentSettings.hostPieceSkinWhite = hs.piece_skin_white;
            currentSettings.hostPieceSkinBlack = hs.piece_skin_black;
            currentSettings.hostDukeSkinWhite = hs.duke_skin_white;
            currentSettings.hostDukeSkinBlack = hs.duke_skin_black;
            currentSettings.hostBoardTheme = hs.board_theme;
            // Include piece colors
            currentSettings.hostPieceTheme = hs.piece_theme;
            currentSettings.hostPieceColorWhite = hs.piece_color_white;
            currentSettings.hostPieceColorBlack = hs.piece_color_black;
            currentSettings.hostUseCustomPieces = hs.use_custom_pieces;
            currentSettings.hostCoordinateColor = hs.coordinate_color;
            localStorage.setItem('chessKingdomSettings', JSON.stringify(currentSettings));
            
            // Also set global vars for immediate use
            window.chessKingdomHostSettings = hs;
            
            const root = document.documentElement;
            
            // Apply piece colors immediately via CSS variables
            // First check for custom colors, then piece theme
            if (hs.use_custom_pieces && hs.piece_color_white && hs.piece_color_black) {
              root.style.setProperty('--piece-white', hs.piece_color_white);
              root.style.setProperty('--piece-black', hs.piece_color_black);
            } else if (hs.piece_theme) {
              // Apply piece theme using the official theme definitions
              const PIECE_THEMES = [
                { id: 'classic', white: '#ffffff', black: '#2d2d2d' },
                { id: 'red_blue', white: '#e74c3c', black: '#3498db' },
                { id: 'gold_silver', white: '#f1c40f', black: '#95a5a6' },
                { id: 'green_purple', white: '#2ecc71', black: '#9b59b6' },
                { id: 'orange_teal', white: '#e67e22', black: '#1abc9c' },
              ];
              const pieceTheme = PIECE_THEMES.find(t => t.id === hs.piece_theme);
              if (pieceTheme) {
                root.style.setProperty('--piece-white', pieceTheme.white);
                root.style.setProperty('--piece-black', pieceTheme.black);
              } else {
                logger.warn(`🎨 Unknown piece theme: ${hs.piece_theme}`);
              }
            }
            
            // Apply board theme immediately
            if (hs.board_theme && hs.board_theme !== 'default') {
              const BOARD_THEMES = [
                { id: 'classic', light: '#f0d9b5', dark: '#b58863' },
                { id: 'blue', light: '#dee3e6', dark: '#8ca2ad' },
                { id: 'green', light: '#ffffdd', dark: '#86a666' },
                { id: 'purple', light: '#e8d4f2', dark: '#9b59b6' },
                { id: 'brown', light: '#f4e4c1', dark: '#a67c52' },
                { id: 'gray', light: '#e6e6e6', dark: '#888888' },
                { id: 'gold', light: '#f5e6c8', dark: '#c9a227' },
                { id: 'obsidian', light: '#c0c0c0', dark: '#303030' },
                { id: 'pink', light: '#ffe4ec', dark: '#d4849a' },
              ];
              const theme = BOARD_THEMES.find(t => t.id === hs.board_theme);
              if (theme) {
                root.style.setProperty('--board-light', theme.light);
                root.style.setProperty('--board-dark', theme.dark);
              }
            }
            
            // Apply coordinate color
            if (hs.coordinate_color) {
              root.style.setProperty('--coordinate-color', hs.coordinate_color);
            }
          } catch (e) {
            logger.warn('Failed to save host settings:', e);
          }
        }
        
        loadJoinedRooms(); // Refresh joined rooms list
        
        // Always go to waiting room (whether active or waiting)
        setSuccess(`Joined room successfully! You are ${data.color.toUpperCase()}`);
        setTimeout(() => {
          handleGameRedirect(data.game_id, 'multiplayer', data.room_name);
        }, 1000);
        
        setRoomCode(''); // Clear code input
      } else {
        // Handle specific error codes
        if (response.status === 503 && data.offline) {
          // Service worker offline response
          setError(t('errors.offlineError', 'You appear to be offline. Please check your internet connection and try again.'));
        } else if (response.status === 403) {
          setError(data.detail || t('errors.roomRequiresApproval'));
        } else if (response.status === 404) {
          setError(t('errors.roomNotFound'));
        } else {
          setError(data.detail || t('errors.failedToJoinRoom'));
        }
      }
    } catch (error) {
      logger.error('Join room error:', error);
      // Provide more specific error messages
      if (!navigator.onLine) {
        setError(t('errors.offlineError', 'You appear to be offline. Please check your internet connection and try again.'));
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError(t('errors.networkError', 'Unable to connect to server. Please reload the page and try again.'));
      } else {
        setError(t('errors.connectionError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess(t('success.copiedToClipboard'));
    setTimeout(() => setSuccess(''), 2000);
  };

  // Request rejoin to a room
  const handleRequestRejoin = async (gameId) => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/request-rejoin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(data.message || t('success.rejoinRequestSent'));
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.detail || t('errors.failedToRequestRejoin'));
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    }
  };

  // Remove room from joined rooms list
  const handleRemoveJoinedRoom = async (gameId) => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/remove-joined`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Remove from local state
        setJoinedRooms(prev => prev.filter(room => room.game_id !== gameId));
        setSuccess(t('success.roomRemovedFromList'));
        setTimeout(() => setSuccess(''), 2000);
      } else {
        const data = await response.json();
        setError(data.detail || t('errors.failedToRemoveRoom'));
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    }
  };

  // Load spectatable games
  const loadSpectateGames = async () => {
    clearMessages();
    setLoading(true);

    try {
      const url = variantFilter 
        ? `${API_URL}/api/multiplayer/spectate/games?variant=${variantFilter}`
        : `${API_URL}/api/multiplayer/spectate/games`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        // Filter out games where the current user is a player
        const filteredGames = (data.games || []).filter(game => 
          game.white_player_id !== user?.user_id && 
          game.black_player_id !== user?.user_id
        );
        setSpectateGames(filteredGames);
        setError(''); // Clear any previous error
      } else {
        setError(data.detail || t('errors.failedToLoadGames'));
      }
    } catch (error) {
      logger.error('📡 Spectate games error:', error);
      setError(t('errors.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Load my created rooms
  const loadMyRooms = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/my-rooms`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setMyRooms([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setMyRooms(data.rooms || []);
    } catch (error) {
      logger.error('Failed to load my rooms:', error);
      setMyRooms([]); // Set empty array on error
    }
  }, [token, API_URL]);

  // Load joined rooms
  const loadJoinedRooms = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/joined-rooms`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setJoinedRooms([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setJoinedRooms(data.rooms || []);
    } catch (error) {
      logger.error('Failed to load joined rooms:', error);
      setJoinedRooms([]); // Set empty array on error
    }
  }, [token, API_URL]);

  // Leave a room
  const handleLeaveRoom = async (gameId) => {
    if (!window.confirm('Are you sure you want to leave this room?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(t('success.leftRoomSuccessfully'));
        loadJoinedRooms(); // Refresh joined rooms
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.detail || t('errors.failedToLeaveRoom'));
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    }
  };

  // Cancel a room
  const handleCancelRoom = async (gameId) => {
    if (!window.confirm('Are you sure you want to cancel this room?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/cancel`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Room cancelled successfully');
        loadMyRooms(); // Refresh my rooms
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.detail || 'Failed to cancel room');
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    }
  };

  // Handle rematch - reset the existing room instead of creating a new one
  const handleRematch = async (originalRoom) => {
    clearMessages();
    setLoading(true);

    try {
      // Use the new rematch endpoint to reset the existing room
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${originalRoom.game_id}/rematch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Room reset for rematch! Same code: ${data.room_code || originalRoom.room_code}`);
        loadMyRooms(); // Refresh the list
        
        // Redirect to waiting room with the same game_id
        setTimeout(() => {
          handleGameRedirect(originalRoom.game_id, 'multiplayer', originalRoom.room_name);
        }, 1000);
      } else {
        setError(data.detail || 'Failed to reset room for rematch');
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Clear all rooms created by user
  const handleClearAllRooms = async () => {
    if (!window.confirm('Are you sure you want to clear ALL your rooms? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/clear-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Cleared ${data.cleared_count} room(s)`);
        loadMyRooms(); // Refresh my rooms
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.detail || 'Failed to clear rooms');
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    }
  };

  // Clear all joined rooms
  const handleClearJoinedRooms = async () => {
    if (!window.confirm('Are you sure you want to leave ALL joined rooms? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/clear-joined`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Left ${data.cleared_count} room(s)`);
        loadJoinedRooms(); // Refresh joined rooms
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.detail || 'Failed to clear joined rooms');
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    }
  };

  // Load friends list for challenge modal
  const loadFriendsForChallenge = async () => {
    try {
      const response = await fetch(`${API_URL}/api/friends`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFriendsList(data.friends || []);
      }
    } catch (error) {
      logger.error('Failed to load friends:', error);
    }
  };

  // Open challenge modal for a room
  const handleOpenChallengeModal = async (room) => {
    setChallengeRoomId(room);
    await loadFriendsForChallenge();
    setShowChallengeModal(true);
  };

  // Send challenge/invite to a friend
  const handleChallengeFriend = async (friendId) => {
    if (!challengeRoomId) return;

    try {
      const response = await fetch(`${API_URL}/api/friends/invite/${friendId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          room_code: challengeRoomId.room_code,
          game_id: challengeRoomId.game_id,
          variant: challengeRoomId.variant
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Challenge sent! Waiting for ${data.friend_name || 'friend'} to accept.`);
        setShowChallengeModal(false);
        setChallengeRoomId(null);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.detail || 'Failed to send challenge');
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    }
  };

  // Fetch pending challenges
  const fetchPendingChallenges = async () => {
    try {
      const response = await fetch(`${API_URL}/api/friends/challenges/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingChallenges({ incoming: data.incoming || [], outgoing: data.outgoing || [] });
      }
    } catch (error) {
      logger.error('Failed to fetch challenges:', error);
    }
  };

  // Open direct challenge modal (from Challenge tab)
  const handleOpenDirectChallenge = async () => {
    setDirectChallengeMode(true);
    setChallengeRoomId(null);
    await loadFriendsForChallenge();
    setShowChallengeModal(true);
  };

  // Send direct challenge to a friend
  const handleDirectChallengeFriend = async (friendId) => {
    setChallengeProcessing(friendId);
    try {
      const response = await fetch(`${API_URL}/api/friends/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ friend_id: friendId, variant: challengeVariant, move_timer: challengeTimer })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccess(data.message);
        setShowChallengeModal(false);
        setDirectChallengeMode(false);
        fetchPendingChallenges();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(data.detail || 'Failed to send challenge');
      }
    } catch (error) {
      setError('Failed to send challenge');
    } finally {
      setChallengeProcessing(null);
    }
  };

  // Accept incoming challenge
  const handleAcceptChallenge = async (challengeId) => {
    setChallengeProcessing(challengeId);
    try {
      const response = await fetch(`${API_URL}/api/friends/challenge/${challengeId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        handleGameRedirect(data.game_id, 'multiplayer', `Challenge Game`);
      } else {
        setError(data.detail || 'Failed to accept challenge');
      }
    } catch (error) {
      setError('Failed to accept challenge');
    } finally {
      setChallengeProcessing(null);
    }
  };

  // Decline incoming challenge
  const handleDeclineChallenge = async (challengeId) => {
    setChallengeProcessing(challengeId);
    try {
      const response = await fetch(`${API_URL}/api/friends/challenge/${challengeId}/decline`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        fetchPendingChallenges();
      } else {
        setError(data.detail || 'Failed to decline challenge');
      }
    } catch (error) {
      setError('Failed to decline challenge');
    } finally {
      setChallengeProcessing(null);
    }
  };



  // Join game as spectator
  const handleSpectateGame = async (gameId, spectatorMode) => {
    clearMessages();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/spectate/${gameId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        // Check if we need to wait for approval (request mode)
        if (data.message === 'Request sent to players' || spectatorMode === 'request') {
          // Show pending request overlay
          setPendingSpectatorRequest({
            gameId: gameId,
            status: 'pending'
          });
        } else {
          // Direct access - go to spectate
          handleGameRedirect(gameId, 'spectate');
        }
      } else {
        // Handle "already requested" case - show pending overlay
        if (data.detail === 'Request already sent' || data.message === 'Request already sent') {
          setPendingSpectatorRequest({
            gameId: gameId,
            status: 'pending'
          });
        } else {
          setError(data.detail || 'Failed to join as spectator');
        }
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle spectator request approval
  const handleSpectatorApproved = () => {
    if (pendingSpectatorRequest) {
      handleGameRedirect(pendingSpectatorRequest.gameId, 'spectate');
    }
    setPendingSpectatorRequest(null);
  };

  // Handle spectator request denial
  const handleSpectatorDenied = () => {
    setPendingSpectatorRequest(null);
    setError('Your spectator request was denied.');
  };

  // Cancel pending spectator request
  const handleCancelSpectatorRequest = () => {
    setPendingSpectatorRequest(null);
  };

  // Load spectator requests for rooms where user is a player
  const loadSpectatorRequests = async (gameId) => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/spectator-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSpectatorRequests(prev => ({
          ...prev,
          [gameId]: data.requests || []
        }));
      }
    } catch (error) {
      logger.error('Error loading spectator requests:', error);
    }
  };

  // Approve a spectator request
  const handleApproveSpectator = async (gameId, requesterId) => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/spectate/${gameId}/approve/${requesterId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        // Remove from local state
        setSpectatorRequests(prev => ({
          ...prev,
          [gameId]: (prev[gameId] || []).filter(r => r.user_id !== requesterId)
        }));
        setSuccess('Spectator request approved!');
      }
    } catch (error) {
      setError('Failed to approve spectator request');
    }
  };

  // Deny a spectator request
  const handleDenySpectator = async (gameId, requesterId) => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/spectate/${gameId}/deny/${requesterId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        // Remove from local state
        setSpectatorRequests(prev => ({
          ...prev,
          [gameId]: (prev[gameId] || []).filter(r => r.user_id !== requesterId)
        }));
        setSuccess('Spectator request denied.');
      }
    } catch (error) {
      setError('Failed to deny spectator request');
    }
  };

  // Load spectator requests for all rooms with request mode
  useEffect(() => {
    myRooms.forEach(room => {
      if (room.spectator_mode === 'request' && room.spectator_requests_count > 0) {
        loadSpectatorRequests(room.game_id);
      }
    });
    joinedRooms.forEach(room => {
      if (room.spectator_mode === 'request' && room.spectator_requests_count > 0) {
        loadSpectatorRequests(room.game_id);
      }
    });
  }, [myRooms, joinedRooms]);

  // Approve a rejoin request
  const handleApproveRejoin = async (gameId, requesterId) => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/approve-rejoin/${requesterId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        // Refresh my rooms to update the UI
        loadMyRooms();
        setSuccess('Rejoin request approved!');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to approve rejoin request');
      }
    } catch (error) {
      setError('Failed to approve rejoin request');
    }
  };

  // Deny a rejoin request
  const handleDenyRejoin = async (gameId, requesterId) => {
    try {
      const response = await fetch(`${API_URL}/api/multiplayer/rooms/${gameId}/deny-rejoin/${requesterId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        // Refresh my rooms to update the UI
        loadMyRooms();
        setSuccess('Rejoin request denied.');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to deny rejoin request');
      }
    } catch (error) {
      setError('Failed to deny rejoin request');
    }
  };

  // Create a BOT vs BOT game for spectating
  const handleCreateBotGame = async () => {
    clearMessages();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/multiplayer/bot-game/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          variant: variantFilter || 'instant_king'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`🤖 BOT game started! Click to watch.`);
        loadSpectateGames(); // Refresh the list
        
        // Auto-join as spectator after a short delay
        setTimeout(() => {
          handleSpectateGame(data.game_id);
        }, 1500);
      } else {
        setError(data.detail || 'Failed to create BOT game');
      }
    } catch (error) {
      setError(t('errors.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  // Load spectate games when tab is active
  useEffect(() => {
    if (activeTab === 'spectate') {
      loadSpectateGames();
    } else if (activeTab === 'private') {
      loadMyRooms();
      loadJoinedRooms();
    }
  }, [activeTab]);

  // Reload spectate games when variant filter changes
  useEffect(() => {
    if (activeTab === 'spectate') {
      loadSpectateGames();
    }
  }, [variantFilter]);

  // Handle lobby WebSocket messages using centralized context
  useEffect(() => {
    const handleLobbyMessage = (data) => {
      if (data.type === 'room_joined') {
        // Someone joined your room
        // Add notification to bell
        addNotification(
          'room_joined',
          data.game_id,
          data.room_name,
          `${data.joiner_name} joined (2/2 players)`
        );
        
        setSuccess(`${data.joiner_name} joined your room "${data.room_name}"!`);
        loadMyRooms(); // Refresh room list
        
        // Auto-redirect if room is ready
        if (data.should_redirect) {
          setTimeout(() => {
            handleGameRedirect(data.game_id, 'multiplayer', data.room_name);
          }, 2000); // 2 second delay to show message
        }
      } else if (data.type === 'room_ready') {
        // You joined a room and it's ready
        // Add notification to bell
        addNotification(
          'game_ready',
          data.game_id,
          data.room_name || 'Game Room',
          'Game is ready to start!'
        );
        
        if (data.should_redirect) {
          setSuccess('Room is ready! Starting game...');
          setTimeout(() => {
            handleGameRedirect(data.game_id, 'multiplayer', data.room_name);
          }, 1500);
        }
      } else if (data.type === 'spectator_joined') {
        // Spectator joined your game
        addNotification(
          'spectator_joined',
          data.game_id,
          data.room_name || 'Your Game',
          `${data.spectator_name || 'Someone'} is watching`
        );
      } else if (data.type === 'friend_challenge') {
        // Someone challenged you
        addNotification(
          'friend_challenge',
          data.challenge_id,
          'Challenge',
          data.message || `${data.from_user_name} challenged you!`
        );
        fetchPendingChallenges();
        setSuccess(data.message || `${data.from_user_name} challenged you!`);
        setTimeout(() => setSuccess(''), 5000);
      } else if (data.type === 'challenge_accepted') {
        // Your challenge was accepted
        addNotification(
          'challenge_accepted',
          data.game_id,
          'Challenge Accepted',
          data.message || 'Your challenge was accepted!'
        );
        setSuccess(data.message || 'Challenge accepted! Joining game...');
        fetchPendingChallenges();
        setTimeout(() => {
          handleGameRedirect(data.game_id, 'multiplayer', 'Challenge Game');
        }, 1500);
      } else if (data.type === 'challenge_declined') {
        // Your challenge was declined
        fetchPendingChallenges();
        setError(data.message || 'Challenge was declined.');
        setTimeout(() => setError(''), 5000);
      }
    };

    // Register the handler with the centralized WebSocket
    const unsubscribe = addMessageHandler(handleLobbyMessage);
    
    // Cleanup on unmount
    return () => {
      unsubscribe();
    };
  }, [addMessageHandler, addNotification, handleGameRedirect, loadMyRooms]);

  return (
    <div className="mp-overlay" onClick={onClose}>
      {/* Spectator Request Pending Overlay */}
      {pendingSpectatorRequest && (
        <SpectatorRequestPending
          gameId={pendingSpectatorRequest.gameId}
          onApproved={handleSpectatorApproved}
          onDenied={handleSpectatorDenied}
          onCancel={handleCancelSpectatorRequest}
        />
      )}
      
      <div className="mp-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mp-header">
          <h1>🌐 Multiplayer Lobby</h1>
          <div className="mp-header-actions">
            <button className="mp-close" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mp-tabs">
          <button 
            className={`mp-tab ${activeTab === 'quick' ? 'active' : ''}`}
            onClick={() => { setActiveTab('quick'); clearMessages(); }}
          >
            <span className="tab-icon">⚡</span>
            <span className="tab-label">Quick Match</span>
          </button>
          <button 
            className={`mp-tab ${activeTab === 'private' ? 'active' : ''}`}
            onClick={() => { setActiveTab('private'); clearMessages(); }}
          >
            <span className="tab-icon">🎯</span>
            <span className="tab-label">Create Room</span>
          </button>
          <button 
            className={`mp-tab ${activeTab === 'join' ? 'active' : ''}`}
            onClick={() => { setActiveTab('join'); clearMessages(); setRoomCode(''); }}
          >
            <span className="tab-icon">🔑</span>
            <span className="tab-label">Join Room</span>
          </button>
          <button 
            className={`mp-tab ${activeTab === 'spectate' ? 'active' : ''}`}
            onClick={() => { setActiveTab('spectate'); clearMessages(); }}
          >
            <span className="tab-icon">👁️</span>
            <span className="tab-label">Spectate</span>
          </button>
          <button 
            className={`mp-tab ${activeTab === 'challenge' ? 'active' : ''}`}
            onClick={() => { setActiveTab('challenge'); clearMessages(); fetchPendingChallenges(); }}
            data-testid="tab-challenge"
          >
            <span className="tab-icon">⚔️</span>
            <span className="tab-label">Challenge</span>
            {pendingChallenges.incoming.length > 0 && (
              <span className="challenge-badge" data-testid="challenge-badge">{pendingChallenges.incoming.length}</span>
            )}
          </button>
        </div>

        {/* Content Area */}
        <div className="mp-content">
          {/* Messages */}
          {error && (
            <div className="mp-message mp-error">
              <span className="msg-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="mp-message mp-success">
              <span className="msg-icon">✓</span>
              <span>{success}</span>
            </div>
          )}

          {/* Quick Match Tab */}
          {activeTab === 'quick' && (
            <div className="mp-panel">
              <div className="panel-icon-large">⚡</div>
              <h2>Quick Match</h2>
              <p className="panel-description">
                Choose your game mode and find an opponent instantly
              </p>

              {isSearching ? (
                <div className="searching-state">
                  <div className="spinner"></div>
                  <p className="searching-text">Searching for opponent...</p>
                  <p className="searching-subtext">Game mode: {variant.replace('_', ' ')}</p>
                  <button 
                    className="mp-button secondary"
                    onClick={cancelSearch}
                  >
                    Cancel Search
                  </button>
                </div>
              ) : (
                <>
                  <div className="game-mode-grid">
                    <div 
                      className={`game-mode-card ${variant === 'standard' ? 'selected' : ''}`}
                      onClick={() => setVariant('standard')}
                    >
                      <div className="mode-icon">♟️</div>
                      <div className="mode-name">Standard Chess</div>
                      <div className="mode-desc">Classic chess rules</div>
                    </div>
                    
                    <div 
                      className={`game-mode-card ${variant === 'fight_to_be_king' ? 'selected' : ''}`}
                      onClick={() => setVariant('fight_to_be_king')}
                    >
                      <div className="mode-icon">⚔️</div>
                      <div className="mode-name">Fight to be King</div>
                      <div className="mode-desc">Battle for the crown</div>
                    </div>

                    <div 
                      className={`game-mode-card premium-card ${hasPremium && variant === 'instant_king' ? 'selected' : ''} ${!hasPremium ? 'locked-mode' : ''}`}
                      onClick={() => hasPremium && setVariant('instant_king')}
                      data-testid="quick-match-instant-king"
                    >
                      {!hasPremium && <div className="premium-lock-badge">FULL ACCESS</div>}
                      <div className="mode-icon">👑</div>
                      <div className="mode-name">Instant King</div>
                      <div className="mode-desc">{hasPremium ? 'Fast-paced action' : '$4.99/mo to unlock'}</div>
                    </div>
                  </div>

                  <button 
                    className="mp-button primary large"
                    onClick={handleQuickMatch}
                    disabled={loading}
                  >
                    {loading ? 'Starting...' : '⚡ Find Match'}
                  </button>

                  <div className="quick-match-info">
                    <p className="info-text">
                      Selected: <strong>{variant.replace('_', ' ')}</strong>
                    </p>
                    <p className="info-subtext">
                      You'll be matched with another player looking for the same game mode
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Create Private Room Tab */}
          {activeTab === 'private' && (
            <div className="mp-panel">
              <div className="panel-icon-large">🎯</div>
              <h2>Create Private Room</h2>
              <p className="panel-description">
                Create a room and invite your friends with a code
              </p>

              {!createdRoomCode ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Room Name</label>
                    <input 
                      type="text"
                      className="form-input"
                      placeholder="e.g., Epic Battle"
                      value={roomName}
                      onChange={(e) => setRoomName(e.target.value)}
                      maxLength={30}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Game Variant</label>
                    <select 
                      className="form-select"
                      value={variant} 
                      onChange={(e) => {
                        const v = e.target.value;
                        if (FREE_VARIANTS.includes(v) || hasPremium) setVariant(v);
                      }}
                    >
                      <option value="standard">♟️ Standard Chess</option>
                      <option value="fight_to_be_king">⚔️ Fight to be King</option>
                      <option value="instant_king" disabled={!hasPremium}>{!hasPremium ? '🔒' : '👑'} Instant King {!hasPremium ? '(Premium)' : ''}</option>
                      <option value="two_dukes" disabled={!hasPremium}>{!hasPremium ? '🔒' : '🎭'} Two Dukes {!hasPremium ? '(Premium)' : ''}</option>
                      <option value="players_choice" disabled={!hasPremium}>{!hasPremium ? '🔒' : '🎲'} Player's Choice {!hasPremium ? '(Premium)' : ''}</option>
                      <option value="pawns_wild" disabled={!hasPremium}>{!hasPremium ? '🔒' : '🔥'} Pawn's Wild {!hasPremium ? '(Premium)' : ''}</option>
                      <option value="handicap_mode" disabled={!hasPremium}>{!hasPremium ? '🔒' : '⚖️'} Standard Chess vs Royal Chess Kingdom {!hasPremium ? '(Premium)' : ''}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Spectator Settings</label>
                    <select 
                      className="form-select"
                      value={spectatorMode}
                      onChange={(e) => setSpectatorMode(e.target.value)}
                    >
                      <option value="private">🔒 Private - No Spectators</option>
                      <option value="request">📩 Request to Join</option>
                      <option value="open">🌐 Open to All</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Your Piece Color</label>
                    <div className="color-choice-grid">
                      <div 
                        className={`color-choice-card ${preferredColor === 'white' ? 'selected' : ''}`}
                        onClick={() => setPreferredColor('white')}
                      >
                        <span className="color-icon">⚪</span>
                        <span className="color-name">White</span>
                        <span className="color-desc">Move first</span>
                      </div>
                      <div 
                        className={`color-choice-card ${preferredColor === 'black' ? 'selected' : ''}`}
                        onClick={() => setPreferredColor('black')}
                      >
                        <span className="color-icon">⚫</span>
                        <span className="color-name">Black</span>
                        <span className="color-desc">Move second</span>
                      </div>
                      <div 
                        className={`color-choice-card ${preferredColor === 'random' ? 'selected' : ''}`}
                        onClick={() => setPreferredColor('random')}
                      >
                        <span className="color-icon">🎲</span>
                        <span className="color-name">Random</span>
                        <span className="color-desc">Surprise me!</span>
                      </div>
                    </div>
                  </div>

                  {/* Move Timer Selection */}
                  <div className="form-group timer-section">
                    <label>⏱️ Move Timer (Optional)</label>
                    <p className="form-hint">Set a time limit per move. If a player runs out of time, they lose their turn.</p>
                    <div className="timer-options">
                      <div 
                        className={`timer-option ${moveTimer === null ? 'selected' : ''}`}
                        onClick={() => setMoveTimer(null)}
                      >
                        <span className="timer-icon">♾️</span>
                        <span className="timer-label">No Limit</span>
                      </div>
                      <div 
                        className={`timer-option ${moveTimer === 15 ? 'selected' : ''}`}
                        onClick={() => setMoveTimer(15)}
                      >
                        <span className="timer-icon">⚡</span>
                        <span className="timer-label">15s</span>
                      </div>
                      <div 
                        className={`timer-option ${moveTimer === 30 ? 'selected' : ''}`}
                        onClick={() => setMoveTimer(30)}
                      >
                        <span className="timer-icon">🏃</span>
                        <span className="timer-label">30s</span>
                      </div>
                      <div 
                        className={`timer-option ${moveTimer === 60 ? 'selected' : ''}`}
                        onClick={() => setMoveTimer(60)}
                      >
                        <span className="timer-icon">🚶</span>
                        <span className="timer-label">1 min</span>
                      </div>
                      <div 
                        className={`timer-option ${moveTimer === 120 ? 'selected' : ''}`}
                        onClick={() => setMoveTimer(120)}
                      >
                        <span className="timer-icon">🧘</span>
                        <span className="timer-label">2 min</span>
                      </div>
                      <div 
                        className={`timer-option ${moveTimer === 300 ? 'selected' : ''}`}
                        onClick={() => setMoveTimer(300)}
                      >
                        <span className="timer-icon">🐢</span>
                        <span className="timer-label">5 min</span>
                      </div>
                    </div>
                  </div>

                  <button 
                    className="mp-button primary large"
                    onClick={handleCreatePrivateRoom}
                    disabled={loading || !roomName.trim()}
                  >
                    {loading ? 'Creating Room...' : 'Create Private Room'}
                  </button>

                  {/* My Created Rooms Section */}
                  {myRooms.length > 0 && (
                    <div className="my-rooms-section">
                      <div className="my-rooms-header">
                        <h3 className="my-rooms-title">📝 My Rooms (Created) - {myRooms.length}</h3>
                        <button 
                          className="clear-all-rooms-btn"
                          onClick={handleClearAllRooms}
                          title="Clear all rooms"
                        >
                          🗑️ Clear All
                        </button>
                      </div>
                      <div className="my-rooms-list">
                        {myRooms.map((room) => (
                          <div key={room.game_id} className="my-room-card">
                            <div className="room-info">
                              <div className="room-header">
                                <span className="room-name">{room.room_name}</span>
                                <span className={`room-status-badge ${room.status}`}>{room.status}</span>
                              </div>
                              <div className="room-details">
                                <span>{room.variant.replace('_', ' ')}</span>
                                {room.room_code && (
                                  <>
                                    <span>•</span>
                                    <span>Code: {room.room_code}</span>
                                  </>
                                )}
                                <span>•</span>
                                <span>{room.player_count}/2 players</span>
                                {/* Win/Loss Stats */}
                                {(room.games_played > 0 || room.host_wins > 0 || room.guest_wins > 0) && (
                                  <>
                                    <span>•</span>
                                    <span className="win-loss-stats" title="Host Wins / Guest Wins">
                                      🏆 {room.host_wins || 0} - {room.guest_wins || 0}
                                    </span>
                                  </>
                                )}
                                {room.spectator_mode === 'request' && room.spectator_requests_count > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="request-badge">📩 {room.spectator_requests_count} spectator{room.spectator_requests_count > 1 ? 's' : ''}</span>
                                  </>
                                )}
                                {room.rejoin_requests_count > 0 && (
                                  <>
                                    <span>•</span>
                                    <span className="request-badge rejoin">🔄 {room.rejoin_requests_count} rejoin{room.rejoin_requests_count > 1 ? 's' : ''}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="room-actions">
                              {room.status !== 'completed' && room.status !== 'cancelled' && (
                                <button 
                                  className="room-action-btn enter"
                                  onClick={() => handleGameRedirect(room.game_id, 'multiplayer', room.room_name)}
                                >
                                  {room.status === 'waiting' ? 'Enter' : 'Resume'}
                                </button>
                              )}
                              {/* Challenge Friend button - only show for waiting rooms */}
                              {room.status === 'waiting' && room.player_count < 2 && (
                                <button 
                                  className="room-action-btn challenge"
                                  onClick={() => handleOpenChallengeModal(room)}
                                  title="Challenge a friend to this room"
                                >
                                  ⚔️ Challenge
                                </button>
                              )}
                              {room.status === 'completed' && (
                                <button 
                                  className="room-action-btn rematch"
                                  onClick={() => handleRematch(room)}
                                  title="Create a new game with the same settings"
                                >
                                  🔄 Rematch
                                </button>
                              )}
                              <button 
                                className="room-action-btn cancel"
                                onClick={() => handleCancelRoom(room.game_id)}
                                title={room.status === 'completed' ? 'Remove from list' : 'Delete room'}
                              >
                                {room.status === 'completed' || room.status === 'cancelled' ? '🗑️' : '❌'}
                              </button>
                            </div>
                            
                            {/* Spectator Requests Section */}
                            {room.spectator_mode === 'request' && spectatorRequests[room.game_id]?.length > 0 && (
                              <div className="spectator-requests-section">
                                <h4>👁️ Spectator Requests</h4>
                                {spectatorRequests[room.game_id].map((req) => (
                                  <div key={req.user_id} className="spectator-request-card">
                                    <div className="request-user-info">
                                      <span className="request-user-avatar">{req.avatar || '👀'}</span>
                                      <span className="request-user-name">{req.name}</span>
                                    </div>
                                    <div className="request-actions">
                                      <button 
                                        className="approve-btn"
                                        onClick={() => handleApproveSpectator(room.game_id, req.user_id)}
                                      >
                                        ✓ Accept
                                      </button>
                                      <button 
                                        className="deny-btn"
                                        onClick={() => handleDenySpectator(room.game_id, req.user_id)}
                                      >
                                        ✗ Deny
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Rejoin Requests Section */}
                            {room.rejoin_requests?.length > 0 && (
                              <div className="rejoin-requests-section">
                                <h4>🔄 Rejoin Requests</h4>
                                {room.rejoin_requests.map((req) => (
                                  <div key={req.user_id} className="rejoin-request-card">
                                    <div className="request-user-info">
                                      <span className="request-user-avatar">{req.avatar || '👤'}</span>
                                      <span className="request-user-name">{req.name}</span>
                                    </div>
                                    <div className="request-actions">
                                      <button 
                                        className="approve-btn"
                                        onClick={() => handleApproveRejoin(room.game_id, req.user_id)}
                                      >
                                        ✓ Accept
                                      </button>
                                      <button 
                                        className="deny-btn"
                                        onClick={() => handleDenyRejoin(room.game_id, req.user_id)}
                                      >
                                        ✗ Deny
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Joined Rooms Section */}
                  {joinedRooms.length > 0 && (
                    <div className="my-rooms-section">
                      <div className="my-rooms-header">
                        <h3 className="my-rooms-title">🎮 Joined Rooms - {joinedRooms.length}</h3>
                        <button 
                          className="clear-all-rooms-btn"
                          onClick={handleClearJoinedRooms}
                          title="Leave all joined rooms"
                        >
                          🗑️ Clear All
                        </button>
                      </div>
                      <div className="my-rooms-list">
                        {joinedRooms.map((room) => (
                          <div key={room.game_id} className="my-room-card joined">
                            <div className="room-info">
                              <div className="room-header">
                                <span className="room-name">{room.room_name}</span>
                                <span className="room-status-badge">{room.status}</span>
                              </div>
                              <div className="room-details">
                                <span>{room.variant?.replace('_', ' ') || 'Chess'}</span>
                                {room.room_code && (
                                  <>
                                    <span>•</span>
                                    <span className="room-code-badge" title="Click to copy" onClick={() => copyToClipboard(room.room_code)}>
                                      📋 {room.room_code}
                                    </span>
                                  </>
                                )}
                                <span>•</span>
                                <span>Host: {room.host_name} </span>
                                <TitleBadgeInline userId={room.host_user_id} />
                                {room.move_timer && (
                                  <>
                                    <span>•</span>
                                    <span className="room-timer-badge">⏱️ {room.move_timer}s/move</span>
                                  </>
                                )}
                                {/* Win/Loss Stats */}
                                {(room.games_played > 0) && (
                                  <>
                                    <span>•</span>
                                    <span className="win-loss-stats" title="Host Wins / Guest Wins">
                                      🏆 {room.host_wins || 0} - {room.guest_wins || 0}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="room-actions">
                              {room.is_active && room.status !== 'completed' ? (
                                <button 
                                  className="room-action-btn enter"
                                  onClick={() => handleGameRedirect(room.game_id, 'multiplayer', room.room_name)}
                                >
                                  {room.status === 'waiting' ? 'Enter' : 'Resume'}
                                </button>
                              ) : (
                                <button 
                                  className="rejoin-btn"
                                  onClick={() => handleRequestRejoin(room.game_id)}
                                >
                                  🔄 Request Rejoin
                                </button>
                              )}
                              {room.is_active && room.status !== 'completed' && (
                                <button 
                                  className="room-action-btn leave"
                                  onClick={() => handleLeaveRoom(room.game_id)}
                                >
                                  Leave
                                </button>
                              )}
                              <button 
                                className="room-action-btn remove"
                                onClick={() => handleRemoveJoinedRoom(room.game_id)}
                                title="Remove from list"
                              >
                                🗑️ Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="room-created">
                  <div className="success-icon">✓</div>
                  <h3>Room Created Successfully!</h3>
                  
                  <div className="code-display">
                    <label className="code-label">Share this code:</label>
                    <div className="code-box">
                      <span className="code-text">{createdRoomCode}</span>
                      <button 
                        className="code-copy-btn"
                        onClick={() => copyToClipboard(createdRoomCode)}
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  <div className="waiting-status">
                    <div className="status-pulse"></div>
                    <p>Waiting for your friend to join...</p>
                  </div>

                  <button 
                    className="mp-button secondary"
                    onClick={() => { setCreatedRoomCode(''); clearMessages(); }}
                  >
                    Create Another Room
                  </button>
                </div>
              )}

              <div className="info-box">
                <p className="info-title">Private Room Features:</p>
                <ul className="info-list">
                  <li>Share code with specific friends</li>
                  <li>Choose your preferred variant</li>
                  <li>Room stays open until you cancel</li>
                  <li>Only your friend can join with the code</li>
                </ul>
              </div>
            </div>
          )}

          {/* Join Room Tab */}
          {activeTab === 'join' && (
            <div className="mp-panel">
              <div className="panel-icon-large">🔑</div>
              <h2>Join Private Room</h2>
              <p className="panel-description">
                Enter the 6-character room code to join your friend's game
              </p>

              <div className="form-group">
                <label className="form-label">Room Code</label>
                <input 
                  ref={roomCodeInputRef}
                  type="text"
                  className="form-input code-input"
                  value={roomCode}
                  onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength={6}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && roomCode.length === 6) {
                      handleJoinRoom();
                    }
                  }}
                />
                <p className="input-hint">Enter the code your friend shared with you</p>
              </div>

              <button 
                className="mp-button primary large"
                onClick={handleJoinRoom}
                disabled={loading || roomCode.length !== 6}
              >
                {loading ? 'Joining...' : 'Join Room'}
              </button>

              <div className="info-box">
                <p className="info-title">How to join:</p>
                <ul className="info-list">
                  <li>Get the room code from your friend</li>
                  <li>Enter the 6-character code above</li>
                  <li>Click "Join Room" or press Enter</li>
                  <li>Game starts when both players ready</li>
                </ul>
              </div>
            </div>
          )}

          {/* Spectate Tab */}
          {activeTab === 'spectate' && (
            <div className="mp-panel">
              <div className="panel-icon-large">👁️</div>
              <h2>Watch Live Games</h2>
              <p className="panel-description">
                Watch ongoing multiplayer matches in real-time
              </p>

              <div className="spectate-filters">
                <div className="form-group">
                  <label className="form-label">Filter by Game Type</label>
                  <select 
                    className="form-select"
                    value={variantFilter}
                    onChange={(e) => setVariantFilter(e.target.value)}
                  >
                    <option value="">All Games</option>
                    <option value="standard">Standard Chess</option>
                    <option value="instant_king">Instant King</option>
                    <option value="fight_to_be_king">Fight to be King</option>
                    <option value="two_dukes">Two Dukes</option>
                  </select>
                </div>

                <button 
                  className="mp-button secondary"
                  onClick={loadSpectateGames}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>

              {spectateGames.length === 0 ? (
                <div className="empty-state">
                  <p className="empty-icon">🎮</p>
                  <p className="empty-text">No active games to spectate right now</p>
                  <p className="empty-subtext">Check back later when players are online!</p>
                </div>
              ) : (
                <div className="spectate-games-list">
                  {spectateGames.map((game) => (
                    <div key={game.game_id} className="spectate-game-card">
                      <div className="game-card-info">
                        <div className="game-header">
                          <span className="game-title">{game.room_name || 'Game Room'}</span>
                          {game.is_restricted && (
                            <span className="restricted-badge">🔒 Restricted</span>
                          )}
                        </div>
                        <div className="game-variant">
                          <span className="variant-icon">♟️</span>
                          <span className="variant-name">{game.variant.replace('_', ' ')}</span>
                        </div>
                        <div className="game-details">
                          <span className="game-status">{game.status}</span>
                          <span className="spectator-count">
                            👁️ {game.spectator_count} watching
                          </span>
                          {game.spectator_mode === 'request' && (
                            <span className="spectator-mode-badge request">📩 Request Only</span>
                          )}
                          {game.spectator_mode === 'open' && (
                            <span className="spectator-mode-badge open">🔓 Open</span>
                          )}
                        </div>
                      </div>
                      <button 
                        className={`spectate-join-btn ${game.spectator_mode === 'request' ? 'request-mode' : ''}`}
                        onClick={() => handleSpectateGame(game.game_id, game.spectator_mode)}
                        disabled={loading || game.is_restricted}
                      >
                        {game.spectator_mode === 'request' ? 'Request' : 'Watch'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="info-box">
                <p className="info-title">Spectator Mode:</p>
                <ul className="info-list">
                  <li>Watch games in real-time</li>
                  <li>See both players' moves</li>
                  <li>No interaction - view only</li>
                  <li>Join and leave anytime</li>
                </ul>
              </div>
            </div>
          )}

          {/* Challenge Tab */}
          {activeTab === 'challenge' && (
            <div className="mp-panel" data-testid="challenge-panel">
              <div className="panel-icon-large">⚔️</div>
              <h2>Challenge a Friend</h2>
              <p className="panel-description">Send a direct challenge to a friend with your choice of variant and timer</p>

              <button
                className="mp-button primary challenge-new-btn"
                onClick={handleOpenDirectChallenge}
                data-testid="new-challenge-btn"
              >
                ⚔️ New Challenge
              </button>

              {/* Incoming Challenges */}
              {pendingChallenges.incoming.length > 0 && (
                <div className="challenges-section" data-testid="incoming-challenges">
                  <h3 className="challenges-heading">Incoming Challenges</h3>
                  {pendingChallenges.incoming.map(c => (
                    <div key={c.challenge_id} className="challenge-card incoming" data-testid={`challenge-${c.challenge_id}`}>
                      <div className="challenge-card-info">
                        <strong>{c.sender_name}</strong>
                        <span className="challenge-variant">{c.variant?.replace(/_/g, ' ')}</span>
                        <span className="challenge-timer">{c.move_timer ? `${c.move_timer}s timer` : 'No timer'}</span>
                      </div>
                      <div className="challenge-card-actions">
                        <button
                          className="mp-button primary small"
                          onClick={() => handleAcceptChallenge(c.challenge_id)}
                          disabled={challengeProcessing === c.challenge_id}
                          data-testid={`accept-challenge-${c.challenge_id}`}
                        >
                          {challengeProcessing === c.challenge_id ? '...' : 'Accept'}
                        </button>
                        <button
                          className="mp-button secondary small"
                          onClick={() => handleDeclineChallenge(c.challenge_id)}
                          disabled={challengeProcessing === c.challenge_id}
                          data-testid={`decline-challenge-${c.challenge_id}`}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Outgoing Challenges */}
              {pendingChallenges.outgoing.length > 0 && (
                <div className="challenges-section" data-testid="outgoing-challenges">
                  <h3 className="challenges-heading">Sent Challenges</h3>
                  {pendingChallenges.outgoing.map(c => (
                    <div key={c.challenge_id} className="challenge-card outgoing" data-testid={`sent-challenge-${c.challenge_id}`}>
                      <div className="challenge-card-info">
                        <span>Waiting for <strong>{c.receiver_name}</strong></span>
                        <span className="challenge-variant">{c.variant?.replace(/_/g, ' ')}</span>
                        <span className="challenge-timer">{c.move_timer ? `${c.move_timer}s timer` : 'No timer'}</span>
                      </div>
                      <div className="challenge-card-status">Pending...</div>
                    </div>
                  ))}
                </div>
              )}

              {pendingChallenges.incoming.length === 0 && pendingChallenges.outgoing.length === 0 && (
                <div className="empty-state">
                  <p className="empty-text">No pending challenges</p>
                  <p className="empty-subtext">Send a challenge to a friend to get started!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Challenge Friend Modal */}
      {showChallengeModal && (
        <div className="challenge-modal-overlay" onClick={() => { setShowChallengeModal(false); setDirectChallengeMode(false); }}>
          <div className="challenge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="challenge-modal-header">
              <h3>⚔️ Challenge a Friend</h3>
              <button 
                className="close-modal-btn"
                onClick={() => { setShowChallengeModal(false); setDirectChallengeMode(false); }}
              >
                X
              </button>
            </div>
            <div className="challenge-modal-content">
              {!directChallengeMode && challengeRoomId && (
                <div className="challenge-room-info">
                  <p>Room: <strong>{challengeRoomId.room_name}</strong></p>
                  <p>Code: <span className="room-code-highlight">{challengeRoomId.room_code}</span></p>
                </div>
              )}
              {directChallengeMode && (
                <div className="challenge-config" data-testid="challenge-config">
                  <div className="form-group">
                    <label className="form-label">Variant</label>
                    <select className="form-select" value={challengeVariant} onChange={(e) => setChallengeVariant(e.target.value)} data-testid="challenge-variant-select">
                      <option value="instant_king">Instant King</option>
                      <option value="fight_to_be_king">Fight to be King</option>
                      <option value="standard">Standard Chess</option>
                      <option value="players_choice">Players Choice</option>
                      <option value="two_dukes">Two Dukes</option>
                      <option value="handicap_mode">Handicap Mode</option>
                      <option value="pawns_wild">Pawns Wild</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Move Timer</label>
                    <select className="form-select" value={challengeTimer || ''} onChange={(e) => setChallengeTimer(e.target.value ? parseInt(e.target.value) : null)} data-testid="challenge-timer-select">
                      <option value="">No Timer</option>
                      <option value="30">30 seconds</option>
                      <option value="60">1 minute</option>
                      <option value="120">2 minutes</option>
                      <option value="300">5 minutes</option>
                      <option value="600">10 minutes</option>
                    </select>
                  </div>
                </div>
              )}
              <p className="challenge-description">Select a friend to challenge:</p>
              {friendsList.length === 0 ? (
                <p className="no-friends-message">No friends found. Add friends to challenge them!</p>
              ) : (
                <div className="friends-challenge-list">
                  {friendsList.map((friend) => (
                    <div key={friend.friend_id} className="friend-challenge-item">
                      <div className="friend-info">
                        <span className="friend-avatar">{friend.avatar || '👤'}</span>
                        <span className="friend-name">{friend.name}</span>
                        <span className={`friend-status ${friend.is_online ? 'online' : 'offline'}`}>
                          {friend.is_online ? '🟢' : '⚪'}
                        </span>
                      </div>
                      <button 
                        className="challenge-send-btn"
                        onClick={() => directChallengeMode ? handleDirectChallengeFriend(friend.friend_id) : handleChallengeFriend(friend.friend_id)}
                        disabled={challengeProcessing === friend.friend_id}
                        data-testid={`challenge-friend-${friend.friend_id}`}
                      >
                        {challengeProcessing === friend.friend_id ? '...' : '⚔️ Challenge'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiplayerLobby;
